import { measurementSection } from './landingMeasurementSection';
import { authDialogMessage } from './landingProductAuthCopy';
import { buildLandingStartSections } from './landingCatalogSections';
import { finalOwnerCloseCopy } from './landingProductSectionCopy';
import { planJourneyFilmSection } from './landingPlanJourneySection';
import type {
  LandingProductCallbacks,
  LandingProductSnapshot,
} from './landingProductDescriptor.types';
import { simulationComparisonSection, timberPriceSection } from './landingProductEvidenceSections';
import { valuationMethodSection } from './landingValuationMethodSection';
import { featureShowcaseSection } from './landingProductShowcaseSection';
import { heroCarouselCopy, heroForecastTableCopy, heroSlideCopy } from './landingHeroCopy';
import { assortmentLabels } from './landingProductEvidenceData';
import { landingRootState } from './landingProductState';
import type {
  FmProductChapterDescriptor,
  FmProductPageProps,
  FmProductSectionDescriptor,
} from '@/components/ui/FmProductPage';
import { productActionsSection, productChapter } from '@/components/ui/archetypes';
import type { Language } from '@/i18n';
import { defineStoryDescriptorBuilder } from '@/queries/storyQuerySnapshot';

export type {
  LandingAuthIntent,
  LandingProductCallbacks,
  LandingProductSnapshot,
} from './landingProductDescriptor.types';

function finalOwnerCloseSection(
  locale: Language,
  callbacks: LandingProductCallbacks,
): FmProductSectionDescriptor {
  const content = finalOwnerCloseCopy(locale);
  return productActionsSection({
    id: 'landing-final-owner-close',
    role: 'action',
    title: content.title,
    answer: content.answer,
    context: `${content.title} ${content.context}`,
    actions: [
      {
        id: 'landing-final-owner-free',
        label: content.free,
        intent: 'primary',
        onSelect: callbacks.addForest,
      },
      {
        id: 'landing-final-owner-package',
        label: content.package,
        intent: 'secondary',
        onSelect: () => callbacks.selectSku('plot_package'),
      },
    ],
  });
}

// The sealed product archetype is authored as one ordered story so chapter composition stays reviewable.
// eslint-disable-next-line max-lines-per-function
export function buildLandingProductDescriptor(
  snapshot: Readonly<LandingProductSnapshot>,
  callbacks: LandingProductCallbacks,
): FmProductPageProps {
  const copy = snapshot.copy;
  const labels = assortmentLabels(snapshot.locale);
  const forecastTableCopy = heroForecastTableCopy(snapshot.locale);
  const scopeUnit =
    snapshot.locale === 'fi'
      ? 'kuviota'
      : snapshot.locale === 'sv'
        ? 'bestånd'
        : snapshot.locale === 'es'
          ? 'rodales'
          : 'stands';
  const commonState = landingRootState(snapshot, callbacks, 'landing-hero');
  const authMessage = authDialogMessage(snapshot);
  const sections = {
    showcase: featureShowcaseSection(copy, snapshot, callbacks),
    value: valuationMethodSection(snapshot.locale),
    prices: timberPriceSection(copy, snapshot),
    measurementBasis: measurementSection(snapshot.locale),
    simulationComparison: simulationComparisonSection(copy, snapshot),
    planJourney: planJourneyFilmSection(snapshot.locale),
  };
  const startSections = buildLandingStartSections({
    story: snapshot.story,
    loading: snapshot.loading,
    locale: snapshot.locale,
    copy,
    callbacks,
  });
  const startSectionsWithClose = [
    ...startSections,
    finalOwnerCloseSection(snapshot.locale, callbacks),
  ];
  const chapters: [FmProductChapterDescriptor, ...FmProductChapterDescriptor[]] = [
    productChapter({
      id: 'act-independently',
      order: 1,
      title: copy.act.title,
      answer: copy.expert.planning.title,
      dominantSectionId: sections.planJourney.id,
      conversionRole: 'build-trust',
      transitionLabel: copy.understand.title,
      targetChapterId: 'understand',
      sections: [sections.planJourney, sections.simulationComparison],
    }),
    productChapter({
      id: 'understand',
      order: 2,
      title: copy.understand.title,
      answer: copy.understand.answer,
      dominantSectionId: sections.showcase.id,
      conversionRole: 'handoff',
      transitionLabel: copy.start.title,
      targetChapterId: 'start',
      sections: [sections.showcase, sections.measurementBasis, sections.prices, sections.value],
    }),
    productChapter({
      id: 'start',
      order: 3,
      title: copy.start.title,
      answer: copy.start.answer,
      dominantSectionId: 'landing-catalog-plot_package',
      conversionRole: 'convert',
      transitionLabel: copy.start.free.actionLabel,
      sections: startSectionsWithClose as [
        FmProductSectionDescriptor,
        ...FmProductSectionDescriptor[],
      ],
    }),
  ];
  return {
    'data-testid': 'landing-page',
    ariaLabel: copy.labels.page,
    chrome: {
      brandLabel: 'Lehti AI',
      onHome: callbacks.home,
      navigationAriaLabel: copy.chrome.navigationAriaLabel,
      menuAriaLabel: copy.chrome.menuAriaLabel,
      openMenuLabel: copy.chrome.openNavigation,
      closeMenuLabel: copy.chrome.closeNavigation,
      navigation: [
        {
          id: 'chrome-process',
          label: copy.chrome.howItWorks,
          chapterId: 'act-independently',
        },
        {
          id: 'chrome-understand',
          label: copy.chrome.features,
          chapterId: 'understand',
        },
        { id: 'chrome-start', label: copy.chrome.pricing, chapterId: 'start' },
      ],
      language: {
        value: snapshot.locale,
        ariaLabel: copy.chrome.languageAriaLabel,
        options: [
          { value: 'fi', label: 'Suomi' },
          { value: 'en', label: 'English' },
          { value: 'sv', label: 'Svenska' },
          { value: 'es', label: 'Español' },
        ],
        onChange: (value: string) => callbacks.languageChange(value as Language),
      },
      identity: snapshot.identity.authenticated
        ? {
            status: 'signed-in',
            accountLabel: snapshot.identity.accountLabel,
            dashboardLabel: copy.chrome.dashboard,
            logoutLabel: copy.chrome.logout,
            onDashboard: callbacks.dashboard,
            onLogout: callbacks.logout,
          }
        : {
            status: 'signed-out',
            loginLabel: copy.chrome.login,
            registerLabel: copy.chrome.register,
            onLogin: callbacks.login,
            onRegister: callbacks.register,
          },
      authDialog: {
        open: snapshot.authDialog.open,
        mode: snapshot.authDialog.mode,
        ...authMessage,
        onClose: callbacks.closeAuth,
        onAuthenticated: callbacks.authenticated,
      },
    },
    footer: {
      brandLabel: 'Lehti AI',
      description: copy.footer.description,
      legalAriaLabel: copy.footer.legalAriaLabel,
      termsLabel: copy.footer.termsLabel,
      privacyLabel: copy.footer.privacyLabel,
      contactLabel: copy.footer.contactLabel,
      contactEmail: copy.footer.contactEmail,
      buyerLabel: copy.footer.buyerLabel,
      onBuyer: callbacks.revealBuyerPricing,
      allRightsReserved: copy.footer.allRightsReserved,
    },
    hero: {
      id: 'immersive-hero',
      // The public search is usable before the asynchronous story snapshot
      // arrives. Keep its geometry stable instead of inserting a full-width
      // loading card that shifts the conversion control after first paint.
      state: snapshot.loading ? { status: 'ready' } : commonState,
      title: copy.hero.title,
      summary: copy.hero.summary,
      proofPoints: copy.hero.proofPoints,
      mediaIdentity: 'product.forest-hero.backdrop',
      slideMediaIdentities: {
        growth: 'product.forest-hero.backdrop',
        choices: 'product.forest-hero.alternatives',
        'harvest-income': 'product.forest-hero.income',
      },
      slideCopy: heroSlideCopy(snapshot.locale),
      ...heroCarouselCopy(snapshot.locale),
      priceTrend: snapshot.timberPrices.data
        ? {
            points: snapshot.timberPrices.data.points,
            regionLabel: snapshot.timberPrices.data.regionLabel,
            sourceLabel: copy.labels.roughValuePriceSourceName,
            pineLabel: labels.pine,
            pulpLabel: labels.pulp,
          }
        : undefined,
      forecast:
        snapshot.heroForecast.status === 'ready'
          ? {
              status: 'ready',
              propertyId: snapshot.heroForecast.propertyId,
              sourcePlanLabel: snapshot.heroForecast.sourcePlanLabel,
              scopeLabel: `${snapshot.heroForecast.coverageStandCount}/${snapshot.heroForecast.propertyStandCount} ${scopeUnit} · ${new Intl.NumberFormat(snapshot.locale, { maximumFractionDigits: 2 }).format(snapshot.heroForecast.areaHectares)} ha`,
              displayLocale: snapshot.locale,
              copy: {
                ...copy.hero.forecast,
                ...forecastTableCopy,
                incomeNavLabel:
                  snapshot.locale === 'fi'
                    ? 'Hinnat'
                    : snapshot.locale === 'sv'
                      ? 'Priser'
                      : snapshot.locale === 'es'
                        ? 'Precios'
                        : 'Prices',
              },
              activeMetric: snapshot.heroForecastMetric,
              onMetricChange: callbacks.selectHeroForecastMetric,
              growth: snapshot.heroForecast.growth,
              harvestIncome: snapshot.heroForecast.harvestIncome,
              alternatives: snapshot.heroForecast.alternatives,
            }
          : {
              status: 'unavailable',
              propertyId: snapshot.heroForecast.propertyId,
              copy: {
                ...copy.hero.forecast,
                ...forecastTableCopy,
                incomeNavLabel:
                  snapshot.locale === 'fi'
                    ? 'Hinnat'
                    : snapshot.locale === 'sv'
                      ? 'Priser'
                      : snapshot.locale === 'es'
                        ? 'Precios'
                        : 'Prices',
              },
            },
      leadInteraction: {
        kind: 'search',
        purpose: 'property',
        label: copy.hero.searchPlaceholder,
        placeholder: copy.hero.searchPlaceholder,
        value: snapshot.searchValue,
        submitLabel: copy.hero.searchSubmit,
        onValueChange: callbacks.searchChange,
        onSubmit: callbacks.searchSubmit,
      },
      nextSectionLabel: copy.act.title,
    },
    chapters,
  };
}

export const landingProductDescriptorBuilder = defineStoryDescriptorBuilder({
  archetype: 'product',
  build: buildLandingProductDescriptor,
});
